import { useContext } from 'react'

import ToggleListItem from '@components/ToggleListItem'

import {
  Button, ModalContext, SidebarModal, SectionDivider,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'

import { toggleNestedValue } from '@functions/entity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'ManageAgencyFeaturesModal'

type ModalCallbacks = {
  updateAgency: (payload: HandleCallbackActionParams) => void,
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageAgencyFeaturesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateAgency } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agency } = modalPayload

  const {
    callbacks: {
      updateAgency: updateFn,
    },
    updating,
  } = useAgency(agency)

  const agencyFormPayload = useAgencyForm(agency)

  const {
    entityState,
    entityState: { features },
  } = agencyFormPayload

  const {
    alpha,
    campaign_management,
    marketing_dashboard,
    new_order_approval,
  } = features

  const updateAgencyPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Agency Features Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="Manage"
        titleSecondLine="Agency Features"
      />

      <SidebarModal.Content>
        {/* AGENCY OPTIONS */}
        <SectionDivider boxProps={{ marginBottom: 'large' }}>AGENCY OPTIONS</SectionDivider>

        <ToggleListItem
          onClick={() => toggleNestedValue(agencyFormPayload, 'features.alpha', { shouldSetState: true })}
          toggleValue={alpha}
        >
          <ToggleListItem.Title>Alpha Features</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agency will access latest updates to realhub before all agencies
          </ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          onClick={() => toggleNestedValue(agencyFormPayload, 'features.marketing_dashboard', { shouldSetState: true })}
          toggleValue={marketing_dashboard}
        >
          <ToggleListItem.Title>Marketing Dashboard</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agency can access agency marketing dashboard and see it in their navigation
          </ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          onClick={() => toggleNestedValue(agencyFormPayload, 'features.new_order_approval', { shouldSetState: true })}
          toggleValue={new_order_approval}
        >
          <ToggleListItem.Title>New Order Approval</ToggleListItem.Title>

          <ToggleListItem.Description>
            FullScreen order approval page
          </ToggleListItem.Description>
        </ToggleListItem>

        {/* OLD OPTIONS */}
        <SectionDivider boxProps={{ marginBottom: 'large' }}>OLD OPTIONS</SectionDivider>

        <ToggleListItem
          onClick={() => toggleNestedValue(agencyFormPayload, 'features.campaign_management', { shouldSetState: true })}
          toggleValue={campaign_management}
        >
          <ToggleListItem.Title>Campaign Management</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agency can use the system to manage property campaigns
          </ToggleListItem.Description>
        </ToggleListItem>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateAgency(updateAgencyPayload)}
          size="large"
        >
          Save Features
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencyFeaturesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
